import { create } from 'zustand';
import { toast } from 'sonner';
import { doc, setDoc, getDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import type { CRMRecord, CRMField, CRMNote, ISaaSModule } from '@/types/saas';
import { useModuleStore } from './module-store';
import { useAuthStore, getEffectiveUid } from './auth-store';
import { useAppStore } from './app-store';
import { getStorageItem, setStorageItem } from '@/lib/utils/storage';
import { idbCache } from '@/lib/cache/idb-cache';
import { swrFetch } from '@/lib/cache/swr';
import { cacheKeys, TTL } from '@/lib/cache/cache-keys';

/**
 * Fetch fields for the lookup panel. Prefers `getLookupModuleFields` when
 * the SaaS module exposes it (currently HubSpot only), so the lookup card,
 * create form, and update form surface the same rich field set the original
 * app showed — owners / pipelines / pipeline stages / picklist enumerations.
 * Every other CRM falls through to `getModuleFields` (unchanged behavior).
 */
async function fetchLookupFields(
  saasModule: ISaaSModule,
  module: string,
): Promise<CRMField[]> {
  if (saasModule.getLookupModuleFields) {
    const map = await saasModule.getLookupModuleFields(module);
    return Object.values(map);
  }
  return saasModule.getModuleFields(module);
}

/**
 * Per-module "lookup source" mapping. For a source module, follow `lookupField`
 * to its associated record in `targetModule` and surface the chosen phone/email
 * fields as contact options. Configured per-user in Compose View settings.
 */
export interface LookupSourceConfig {
  /** api_name of the lookup field on the source module */
  lookupField: string;
  /** target module the lookup points to (from field.lookup.module) */
  targetModule: string;
  /** chosen phone field api_names on the target module */
  phoneFields: string[];
  /** chosen email field api_name on the target module */
  emailField?: string;
}

// Firestore keys for lookup settings (stored in the personal preferences doc)
const LOOKUP_KEYS = {
  ENABLED_MODULES: 'ua_lookup_enabled_modules',
  VISIBLE_FIELDS: 'ua_lookup_visible_fields',
  FIELD_ORDER: 'ua_lookup_field_order',
  SHOW_BY_DEFAULT: 'ua_lookup_show_by_default',
  SOURCE_CONFIG: 'ua_lookup_source_config',
} as const;

/**
 * Get the Firestore document path for user preferences.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/personal/{uid}
 */
function getLookupPrefsDocPath(): string | null {
  const uid = getEffectiveUid();
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!uid || !extensionName || !orgId || !saasCode) {
    return null;
  }

  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${uid}`;
}

/**
 * Persist a single lookup setting key to Firestore (merge).
 */
async function persistLookupSetting(key: string, value: unknown): Promise<void> {
  const docPath = getLookupPrefsDocPath();
  if (!docPath) return;

  try {
    const docRef = doc(db, docPath);
    await setDoc(docRef, { [key]: value }, { merge: true });
  } catch (error) {
    console.error(`[Lookup] Failed to persist ${key}:`, error);
  }
}

interface LookupStore {
  /** Whether the lookup panel is visible */
  isOpen: boolean;
  /** The currently displayed CRM record */
  record: CRMRecord | null;
  /** Multiple matching records (user picks one) */
  records: CRMRecord[];
  /** Id of the contact/user whose related tickets are currently being
   *  fetched (drives the per-row button spinner). Null when idle. */
  ticketsLoadingForId: string | null;
  /** True when the current `record` was auto-opened because the search returned exactly one match. UI hides "Back to results" in this case since there's no list to return to. */
  autoSelected: boolean;
  /** Field definitions for the active record's module */
  fields: CRMField[];
  /** Loading state for lookup / field fetch */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** The phone number currently being looked up */
  currentPhone: string | null;
  /** apiName of the field currently being edited inline */
  editingField: string | null;
  /** Pending edit values keyed by apiName */
  editValues: Record<string, unknown>;
  /** Whether a field save is in progress */
  saving: boolean;
  /** Whether create-record mode is active */
  createMode: boolean;
  /** Module selected for creating a new record */
  createModule: string | null;
  /** Field definitions for the create module */
  createFields: CRMField[];

  /** Notes for the current record */
  notes: CRMNote[];
  /** Whether notes are being loaded */
  notesLoading: boolean;
  /** Whether a note is being added */
  notesAdding: boolean;

  /** Settings view state */
  settingsOpen: boolean;
  /** Which modules are enabled for lookup — keyed by module apiName */
  enabledModules: Record<string, boolean>;
  /** Which fields are visible per module — keyed by module apiName → field apiName → boolean */
  visibleFields: Record<string, Record<string, boolean>>;
  /** Custom field ordering per module — keyed by module apiName → ordered array of field apiNames */
  fieldOrder: Record<string, string[]>;
  /** Per-module lookup-source mapping — keyed by source module apiName */
  lookupSourceConfig: Record<string, LookupSourceConfig>;
  /** Currently selected module in settings for field config */
  settingsModule: string | null;
  /** Loaded fields for settings module */
  settingsFields: CRMField[];
  /** Whether settings fields are loading */
  settingsFieldsLoading: boolean;

  /** Whether the lookup panel should open by default when navigating to conversations */
  showByDefault: boolean;

  /** Whether lookup settings have been loaded from Firestore */
  lookupSettingsLoaded: boolean;

  /** Standalone lookup search query (LookupView only — separate from phone-driven panel) */
  searchQuery: string;
  /** Field to match against (e.g. 'phone', 'email', or '' for default per-CRM behavior) */
  searchField: string;
  /** Module filter for results display (null = all modules) */
  activeModule: string | null;

  // Actions
  loadLookupSettings(): Promise<void>;
  setShowByDefault(value: boolean): void;
  togglePanel(): void;
  setOpen(open: boolean): void;
  lookupByPhone(phone: string): Promise<void>;
  /**
   * Standalone search for the LookupView (full-page lookup app).
   * Unlike `lookupByPhone`, this never auto-selects a single result —
   * the user always sees the result list grouped by module.
   */
  searchLookup(query: string, field?: string): Promise<void>;
  setActiveModule(module: string | null): void;
  setSearchQuery(query: string): void;
  setSearchField(field: string): void;
  clearSearchResults(): void;
  selectRecord(record: CRMRecord): Promise<void>;
  /**
   * Fetch the tickets related to a contact/user and insert them into the
   * result list right below that record. Driven by the "Load tickets"
   * button on each contact row.
   */
  loadRelatedTickets(record: CRMRecord): Promise<void>;
  startEditing(fieldApiName: string): void;
  cancelEditing(): void;
  setEditValue(fieldApiName: string, value: unknown): void;
  saveField(fieldApiName: string): Promise<boolean>;
  enterCreateMode(module: string): Promise<void>;
  exitCreateMode(): void;
  createRecord(module: string, data: Record<string, unknown>): Promise<boolean>;
  fetchNotes(): Promise<void>;
  addNote(content: string): Promise<boolean>;
  openSettings(): void;
  closeSettings(): void;
  toggleModule(module: string): void;
  selectSettingsModule(module: string): Promise<void>;
  toggleFieldVisibility(module: string, fieldApiName: string): void;
  setAllFieldsVisibility(module: string, visible: boolean, fieldApiNames: string[]): void;
  /** Save (or clear, when `config` is null) the lookup-source mapping for a module */
  setLookupSourceConfig(module: string, config: LookupSourceConfig | null): void;
  reorderField(module: string, fromIndex: number, toIndex: number, fieldApiNames: string[]): void;
  getFieldOrder(module: string): string[];
  isFieldVisible(module: string, fieldApiName: string): boolean;
  reset(): void;
}

/** Sync current lookup settings to IDB so next reload skips Firestore */
function _syncLookupSettingsToIDB(get: () => LookupStore) {
  const ext = useAppStore.getState().extensionName || '';
  const uid = getEffectiveUid() || '';
  if (!ext || !uid) return;
  const { enabledModules, visibleFields, fieldOrder, showByDefault, lookupSourceConfig } = get();
  idbCache.set('org_data', cacheKeys.lookup(ext, uid), { enabledModules, visibleFields, fieldOrder, showByDefault, lookupSourceConfig }, TTL.LOOKUP).catch(() => {});
}

export const useLookupStore = create<LookupStore>((set, get) => ({
  isOpen: getStorageItem('lookup_panel_open', false),
  record: null,
  records: [],
  ticketsLoadingForId: null,
  autoSelected: false,
  fields: [],
  loading: false,
  error: null,
  currentPhone: null,
  editingField: null,
  editValues: {},
  saving: false,
  createMode: false,
  createModule: null,
  createFields: [],
  notes: [],
  notesLoading: false,
  notesAdding: false,
  settingsOpen: false,
  enabledModules: {},
  visibleFields: {},
  fieldOrder: {},
  lookupSourceConfig: {},
  settingsModule: null,
  settingsFields: [],
  settingsFieldsLoading: false,
  // Default to true — new installs see the CRM lookup panel the first time
  // they open a conversation, which makes the integration value immediately
  // obvious. Users who prefer a cleaner chat can turn it off in Settings →
  // Inbox View.
  showByDefault: getStorageItem<boolean>('lookup_show_by_default', true),
  lookupSettingsLoaded: false,
  searchQuery: '',
  searchField: '',
  activeModule: null,

  loadLookupSettings: async () => {
    const docPath = getLookupPrefsDocPath();
    if (!docPath) return;

    const ext = useAppStore.getState().extensionName || '';
    const uid = getEffectiveUid() || '';
    if (!ext || !uid) { set({ lookupSettingsLoaded: true }); return; }

    type LookupSettings = { enabledModules: Record<string, boolean>; visibleFields: Record<string, Record<string, boolean>>; fieldOrder: Record<string, string[]>; showByDefault: boolean; lookupSourceConfig: Record<string, LookupSourceConfig> };

    try {
      const result = await swrFetch<LookupSettings | null>({
        key: cacheKeys.lookup(ext, uid),
        store: 'org_data',
        fetcher: async () => {
          const docRef = doc(db, docPath);
          const snapshot = await getDoc(docRef);
          if (!snapshot.exists()) return null;
          const data = snapshot.data();
          return {
            enabledModules: (data[LOOKUP_KEYS.ENABLED_MODULES] as Record<string, boolean>) || {},
            visibleFields: (data[LOOKUP_KEYS.VISIBLE_FIELDS] as Record<string, Record<string, boolean>>) || {},
            fieldOrder: (data[LOOKUP_KEYS.FIELD_ORDER] as Record<string, string[]>) || {},
            // Default to true when the field hasn't been persisted yet —
            // matches the in-memory default for new installs (see above).
            showByDefault: data[LOOKUP_KEYS.SHOW_BY_DEFAULT] !== false,
            lookupSourceConfig: (data[LOOKUP_KEYS.SOURCE_CONFIG] as Record<string, LookupSourceConfig>) || {},
          };
        },
        ttl: TTL.LOOKUP,
        onRevalidate: (fresh) => {
          if (fresh) {
            set({ ...fresh, lookupSettingsLoaded: true });
            setStorageItem('lookup_show_by_default', fresh.showByDefault);
          }
        },
      });

      if (result) {
        set({ ...result, lookupSettingsLoaded: true });
        setStorageItem('lookup_show_by_default', result.showByDefault);
      } else {
        set({ lookupSettingsLoaded: true });
      }
    } catch (error) {
      console.error('[Lookup] Failed to load settings:', error);
      set({ lookupSettingsLoaded: true });
    }
  },

  setShowByDefault: (value: boolean) => {
    set({ showByDefault: value });
    setStorageItem('lookup_show_by_default', value);
    persistLookupSetting(LOOKUP_KEYS.SHOW_BY_DEFAULT, value);
    _syncLookupSettingsToIDB(get);
  },

  togglePanel: () => {
    const next = !get().isOpen;
    setStorageItem('lookup_panel_open', next);
    set({ isOpen: next });
  },

  setOpen: (open) => {
    setStorageItem('lookup_panel_open', open);
    set({ isOpen: open });
  },

  lookupByPhone: async (phone: string) => {
    const saasModule = useModuleStore.getState().saasModule;
    if (!saasModule) {
      set({ error: 'No CRM module available', loading: false });
      return;
    }

    // Skip if already looking up the same phone
    if (get().currentPhone === phone && (get().record || get().records.length > 0)) {
      return;
    }

    set({
      loading: true,
      error: null,
      record: null,
      records: [],
      ticketsLoadingForId: null,
      autoSelected: false,
      fields: [],
      currentPhone: phone,
      editingField: null,
      editValues: {},
      createMode: false,
      createModule: null,
      createFields: [],
    });

    try {
      const results = await saasModule.searchAcrossModules(phone, 'phone');

      if (results.length === 0) {
        set({ loading: false, records: [] });
        return;
      }

      if (results.length === 1) {
        // Auto-select the single result
        set({ loading: false });
        await get().selectRecord(results[0]);
        set({ autoSelected: true });
        return;
      }

      // Multiple matches — let user pick
      set({ records: results, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Lookup failed',
      });
    }
  },

  searchLookup: async (query: string, field?: string) => {
    const saasModule = useModuleStore.getState().saasModule;
    const trimmed = query.trim();

    if (!trimmed) {
      // Empty query → reset results without firing a request
      set({
        records: [], ticketsLoadingForId: null, autoSelected: false, record: null, fields: [], loading: false, error: null,
        currentPhone: null, searchQuery: '', searchField: field ?? '',
        editingField: null, editValues: {}, createMode: false,
        createModule: null, createFields: [],
      });
      return;
    }

    if (!saasModule) {
      set({ error: 'No CRM module available', loading: false });
      return;
    }

    set({
      loading: true,
      error: null,
      record: null,
      records: [],
      ticketsLoadingForId: null,
      autoSelected: false,
      fields: [],
      // Reuse currentPhone slot so existing lookupByPhone-aware code (e.g.
      // create-record auto-fill) keeps working when the user searched by phone.
      currentPhone: trimmed,
      searchQuery: trimmed,
      searchField: field ?? '',
      editingField: null,
      editValues: {},
      createMode: false,
      createModule: null,
      createFields: [],
    });

    try {
      const results = await saasModule.searchAcrossModules(trimmed, field || undefined);

      if (results.length === 1) {
        set({ loading: false });
        await get().selectRecord(results[0]);
        set({ autoSelected: true });
        return;
      }

      set({ records: results, loading: false });
    } catch (err) {
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Search failed',
      });
    }
  },

  setActiveModule: (module) => set({ activeModule: module }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSearchField: (field) => set({ searchField: field }),
  clearSearchResults: () => set({
    records: [], ticketsLoadingForId: null, autoSelected: false, record: null, fields: [], error: null,
    editingField: null, editValues: {}, createMode: false,
    createModule: null, createFields: [], notes: [],
  }),

  selectRecord: async (record: CRMRecord) => {
    const saasModule = useModuleStore.getState().saasModule;
    set({
      record,
      records: [],
      ticketsLoadingForId: null,
      autoSelected: false,
      fields: [],
      editingField: null,
      editValues: {},
      createMode: false,
      notes: [],
      notesLoading: false,
    });

    if (!saasModule) return;

    const ext = useAppStore.getState().extensionName || '';
    const fieldsCacheKey = `module_fields_${ext}_${record.module}`;

    try {
      // Check IDB cache for module fields first
      const cachedFields = await idbCache.get<CRMField[]>('org_data', fieldsCacheKey);
      if (cachedFields && cachedFields.length > 0) {
        console.log(`[Lookup] IDB HIT → ${cachedFields.length} fields for ${record.module}`);
        set({ fields: cachedFields });
        // Background refresh
        fetchLookupFields(saasModule, record.module).then((fields) => {
          set({ fields });
          idbCache.set('org_data', fieldsCacheKey, fields, 24 * 60 * 60 * 1000).catch(() => {});
        }).catch(() => {});
      } else {
        const fields = await fetchLookupFields(saasModule, record.module);
        set({ fields });
        if (fields.length > 0) {
          idbCache.set('org_data', fieldsCacheKey, fields, 24 * 60 * 60 * 1000).catch(() => {});
        }
      }
    } catch {
      // Fields are optional — record still displays with raw data
    }

    // Some CRMs' search endpoints return abbreviated records (id/name/
    // email only, or with several fields omitted). Re-fetch by id in the
    // background and merge so the full set of populated fields renders.
    // Harmless when the search response was already complete — the merge
    // is a no-op.
    saasModule.fetchRecord(record.module, record.id).then((full) => {
      const current = get().record;
      if (!current || current.id !== record.id || current.module !== record.module) return;
      set({
        record: {
          ...current,
          ...full,
          data: { ...(current.data || {}), ...(full.data || {}) },
        },
      });
    }).catch(() => {});

    // Fetch notes in background (non-blocking)
    get().fetchNotes();
  },

  loadRelatedTickets: async (record: CRMRecord) => {
    const saasModule = useModuleStore.getState().saasModule;
    if (!saasModule?.fetchRelatedTickets) return;

    set({ ticketsLoadingForId: record.id });
    try {
      const tickets = await saasModule.fetchRelatedTickets(record);
      if (tickets.length === 0) {
        toast.info('No related tickets found');
        return;
      }
      // Drop any of these tickets already in the list so a repeat click
      // doesn't duplicate rows, then splice the fresh set in right after
      // the contact they belong to.
      const ticketKeys = new Set(tickets.map((t) => `${t.module}_${t.id}`));
      const deduped = get().records.filter((r) => !ticketKeys.has(`${r.module}_${r.id}`));
      const idx = deduped.findIndex((r) => r.module === record.module && r.id === record.id);
      const merged = idx === -1
        ? [...deduped, ...tickets]
        : [...deduped.slice(0, idx + 1), ...tickets, ...deduped.slice(idx + 1)];
      set({ records: merged, activeModule: null });
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      set({ ticketsLoadingForId: null });
    }
  },

  startEditing: (fieldApiName: string) => {
    const { record, fields } = get();
    if (!record) return;

    // Don't allow editing read-only fields
    const field = fields.find((f) => f.apiName === fieldApiName);
    if (field?.readOnly) return;

    const currentValue = record.data[fieldApiName];
    set({
      editingField: fieldApiName,
      editValues: { ...get().editValues, [fieldApiName]: currentValue ?? '' },
    });
  },

  cancelEditing: () => {
    set({ editingField: null });
  },

  setEditValue: (fieldApiName: string, value: unknown) => {
    set({ editValues: { ...get().editValues, [fieldApiName]: value } });
  },

  saveField: async (fieldApiName: string) => {
    const { record, editValues } = get();
    const saasModule = useModuleStore.getState().saasModule;
    if (!record || !saasModule) return false;

    const value = editValues[fieldApiName];

    set({ saving: true });
    try {
      const success = await saasModule.updateRecord(record.module, record.id, {
        [fieldApiName]: value,
      });

      if (success) {
        // Update local record data
        set({
          record: {
            ...record,
            data: { ...record.data, [fieldApiName]: value },
          },
          editingField: null,
          saving: false,
        });
        return true;
      } else {
        set({ saving: false, error: 'Failed to save field' });
        return false;
      }
    } catch (err) {
      set({
        saving: false,
        error: err instanceof Error ? err.message : 'Save failed',
      });
      return false;
    }
  },

  enterCreateMode: async (module: string) => {
    const saasModule = useModuleStore.getState().saasModule;
    set({
      createMode: true,
      createModule: module,
      createFields: [],
      editValues: {},
      error: null,
    });

    if (!saasModule) return;

    const ext = useAppStore.getState().extensionName || '';
    const fieldsCacheKey = `module_fields_${ext}_${module}`;

    try {
      // Check IDB cache for module fields first
      let fields: CRMField[];
      const cachedFields = await idbCache.get<CRMField[]>('org_data', fieldsCacheKey);
      if (cachedFields && cachedFields.length > 0) {
        fields = cachedFields;
        // Background refresh
        fetchLookupFields(saasModule, module).then((fresh) => {
          set({ createFields: fresh });
          idbCache.set('org_data', fieldsCacheKey, fresh, 24 * 60 * 60 * 1000).catch(() => {});
        }).catch(() => {});
      } else {
        fields = await fetchLookupFields(saasModule, module);
        if (fields.length > 0) {
          idbCache.set('org_data', fieldsCacheKey, fields, 24 * 60 * 60 * 1000).catch(() => {});
        }
      }

      // Match phone fields by dataType OR by the module's declared phone
      // field names — some CRMs report phone fields as plain `text` in
      // their schema, so dataType alone isn't enough.
      const phone = get().currentPhone;
      const phoneFieldNames = (saasModule.getPhoneFieldNames?.() ?? ['phone', 'mobile'])
        .map((n) => n.toLowerCase());
      const phoneField = fields.find((f) => {
        if (f.dataType === 'phone') return true;
        return phoneFieldNames.includes(f.apiName.toLowerCase());
      });
      const prefilled: Record<string, unknown> = {};
      if (phone && phoneField) {
        prefilled[phoneField.apiName] = phone.startsWith('+') ? phone : `+${phone}`;
      }

      set({ createFields: fields, editValues: prefilled });
    } catch {
      // Continue without field definitions
    }
  },

  exitCreateMode: () => {
    set({
      createMode: false,
      createModule: null,
      createFields: [],
      editValues: {},
    });
  },

  createRecord: async (module: string, data: Record<string, unknown>) => {
    const saasModule = useModuleStore.getState().saasModule;
    if (!saasModule) return false;

    set({ saving: true, error: null });
    try {
      const recordId = await saasModule.createRecord(module, data);
      if (recordId) {
        set({ saving: false, createMode: false, createModule: null, createFields: [] });
        // Re-lookup to show the newly created record
        const phone = get().currentPhone;
        if (phone) {
          await get().lookupByPhone(phone);
        }
        return true;
      } else {
        set({ saving: false, error: 'Failed to create record' });
        return false;
      }
    } catch (err) {
      set({
        saving: false,
        error: err instanceof Error ? err.message : 'Create failed',
      });
      return false;
    }
  },

  fetchNotes: async () => {
    const { record } = get();
    const saasModule = useModuleStore.getState().saasModule;
    if (!record || !saasModule) return;

    // Zoho Recruit has no notes API surface we support — skip the fetch.
    if (useAppStore.getState().appCodeSAAS === 'zohorecruit') {
      set({ notes: [], notesLoading: false });
      return;
    }

    const ext = useAppStore.getState().extensionName || '';
    const notesCacheKey = `record_notes_${ext}_${record.module}_${record.id}`;

    // Check IDB cache first
    const cachedNotes = await idbCache.get<CRMNote[]>('org_data', notesCacheKey);
    if (cachedNotes) {
      set({ notes: cachedNotes, notesLoading: false });
      // Background refresh
      saasModule.getRecordNotes(record.module, record.id).then((notes) => {
        set({ notes });
        idbCache.set('org_data', notesCacheKey, notes, 5 * 60 * 1000).catch(() => {});
      }).catch(() => {});
      return;
    }

    set({ notesLoading: true });
    try {
      const notes = await saasModule.getRecordNotes(record.module, record.id);
      set({ notes, notesLoading: false });
      // Cache with 5min TTL
      idbCache.set('org_data', notesCacheKey, notes, 5 * 60 * 1000).catch(() => {});
    } catch {
      set({ notes: [], notesLoading: false });
    }
  },

  addNote: async (content: string) => {
    const { record } = get();
    const saasModule = useModuleStore.getState().saasModule;
    if (!record || !saasModule) return false;

    set({ notesAdding: true });
    try {
      const success = await saasModule.addRecordNote(record.module, record.id, content);
      if (success) {
        // Invalidate notes cache before re-fetching
        const ext = useAppStore.getState().extensionName || '';
        idbCache.delete('org_data', `record_notes_${ext}_${record.module}_${record.id}`).catch(() => {});
        // Re-fetch notes to get the latest list
        await get().fetchNotes();
      } else {
        toast.error('Failed to add note');
      }
      set({ notesAdding: false });
      return success;
    } catch (err) {
      // SaaS modules may throw with a user-readable message (e.g. when an
      // endpoint isn't available). Surface that text instead of "failed".
      const message = err instanceof Error ? err.message : 'Failed to add note';
      toast.error(message);
      set({ notesAdding: false });
      return false;
    }
  },

  openSettings: () => {
    set({ settingsOpen: true, settingsModule: null, settingsFields: [] });
  },

  closeSettings: () => {
    set({ settingsOpen: false, settingsModule: null, settingsFields: [] });
  },

  toggleModule: (module: string) => {
    const current = get().enabledModules;
    // Default is enabled (true) when not in map, so toggle to false
    const isCurrentlyEnabled = current[module] !== false;
    const updated = { ...current, [module]: !isCurrentlyEnabled };
    set({ enabledModules: updated });
    persistLookupSetting(LOOKUP_KEYS.ENABLED_MODULES, updated);
    _syncLookupSettingsToIDB(get);
  },

  selectSettingsModule: async (module: string) => {
    if (!module) {
      set({ settingsModule: null, settingsFields: [], settingsFieldsLoading: false });
      return;
    }
    set({ settingsModule: module, settingsFields: [], settingsFieldsLoading: true });
    const saasModule = useModuleStore.getState().saasModule;
    if (!saasModule) {
      set({ settingsFieldsLoading: false });
      return;
    }

    const ext = useAppStore.getState().extensionName || '';
    const fieldsCacheKey = `module_fields_${ext}_${module}`;

    try {
      // Check IDB cache first
      const cachedFields = await idbCache.get<CRMField[]>('org_data', fieldsCacheKey);
      if (cachedFields && cachedFields.length > 0) {
        set({ settingsFields: cachedFields, settingsFieldsLoading: false });
        // Background refresh
        fetchLookupFields(saasModule, module).then((fresh) => {
          set({ settingsFields: fresh });
          idbCache.set('org_data', fieldsCacheKey, fresh, 24 * 60 * 60 * 1000).catch(() => {});
        }).catch(() => {});
      } else {
        const fields = await fetchLookupFields(saasModule, module);
        set({ settingsFields: fields, settingsFieldsLoading: false });
        if (fields.length > 0) {
          idbCache.set('org_data', fieldsCacheKey, fields, 24 * 60 * 60 * 1000).catch(() => {});
        }
      }
    } catch {
      set({ settingsFields: [], settingsFieldsLoading: false });
    }
  },

  toggleFieldVisibility: (module: string, fieldApiName: string) => {
    const current = get().visibleFields;
    const moduleFields = current[module] || {};
    // Default is visible (true) when not in map, so toggle to false
    const isCurrentlyVisible = moduleFields[fieldApiName] !== false;
    const updated = {
      ...current,
      [module]: { ...moduleFields, [fieldApiName]: !isCurrentlyVisible },
    };
    set({ visibleFields: updated });
    persistLookupSetting(LOOKUP_KEYS.VISIBLE_FIELDS, updated);
    _syncLookupSettingsToIDB(get);
  },

  setAllFieldsVisibility: (module: string, visible: boolean, fieldApiNames: string[]) => {
    const current = get().visibleFields;
    const moduleFields: Record<string, boolean> = {};
    for (const name of fieldApiNames) {
      moduleFields[name] = visible;
    }
    const updated = { ...current, [module]: moduleFields };
    set({ visibleFields: updated });
    persistLookupSetting(LOOKUP_KEYS.VISIBLE_FIELDS, updated);
    _syncLookupSettingsToIDB(get);
  },

  setLookupSourceConfig: (module: string, config: LookupSourceConfig | null) => {
    const current = get().lookupSourceConfig;
    const updated = { ...current };
    if (config) {
      updated[module] = config;
    } else {
      delete updated[module];
    }
    set({ lookupSourceConfig: updated });
    persistLookupSetting(LOOKUP_KEYS.SOURCE_CONFIG, updated);
    _syncLookupSettingsToIDB(get);
  },

  reorderField: (module: string, fromIndex: number, toIndex: number, fieldApiNames: string[]) => {
    const ordered = [...fieldApiNames];
    const [moved] = ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, moved);
    const current = get().fieldOrder;
    const updated = { ...current, [module]: ordered };
    set({ fieldOrder: updated });
    persistLookupSetting(LOOKUP_KEYS.FIELD_ORDER, updated);
    _syncLookupSettingsToIDB(get);
  },

  getFieldOrder: (module: string) => {
    return get().fieldOrder[module] || [];
  },

  isFieldVisible: (module: string, fieldApiName: string) => {
    const moduleFields = get().visibleFields[module];
    // If no config exists for this module, all fields are visible by default
    if (!moduleFields) return true;
    // If field is not in config, it's visible by default
    return moduleFields[fieldApiName] !== false;
  },

  reset: () => {
    set({
      record: null,
      records: [],
      ticketsLoadingForId: null,
      autoSelected: false,
      fields: [],
      loading: false,
      error: null,
      currentPhone: null,
      editingField: null,
      editValues: {},
      saving: false,
      createMode: false,
      createModule: null,
      createFields: [],
      notes: [],
      notesLoading: false,
      notesAdding: false,
      settingsOpen: false,
      settingsModule: null,
      settingsFields: [],
      settingsFieldsLoading: false,
      searchQuery: '',
      searchField: '',
      activeModule: null,
    });
  },
}));
